import { signal } from '@preact/signals-react';
import { calendarScrollTo } from 'Calendar';
import calcTo from 'Calendar/helper';
import { isModeFull } from 'Calendar/styles';
import { DatePicker as DatePickerAnt, Row } from 'antd';
import type { Moment, Moment as MomentType } from 'moment';
import moment from 'utils/moment';
import momentGenerateConfig from 'rc-picker/lib/generate/moment';
import React, { forwardRef, memo, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import aptActions from 'services/actions/apt';
import { useAppDispatch } from 'store/hooks';
import styled from 'styled-components';
import getRangeDates, { IDateRangeItem } from 'utils/getRangeDates';
import IconCalendar from '../assets/Icons/iconDate';
import { aptUIActions } from 'services/reducers/apt';

const handleScrollDate = (dateValue: moment.Moment | null) => {
  if (!dateValue) return calendarScrollTo.value(0);
  let pixel = 0;
  if (dateValue.isSame(moment(), 'date')) {
    pixel = calcTo(moment().format('MM-DD-YYYY HH:mm:ss'));
  }
  calendarScrollTo.value(pixel);
};

const DatePickerAntUI = DatePickerAnt.generatePicker<MomentType>(momentGenerateConfig);

export const dateParamSignal = signal<MomentType | null | undefined>(moment());
const _dates = getRangeDates(moment().format(), moment()?.clone().add(1, 'month').format());
type DatePickerRef = {
  setSelectedDate: (data: MomentType | null) => void;
};
export const calendarDatePickerRef = React.createRef<DatePickerRef>();
const DatePicker = forwardRef<DatePickerRef>((_, ref) => {
  const dispatch = useAppDispatch();
  const [selectedDate, setSelectedDate] = useState<MomentType | null>(dateParamSignal.value || moment());
  const [dates, setDates] = useState<IDateRangeItem[]>(_dates);
  const datesListRef = useRef<HTMLDivElement>(null);

  const datesString = useMemo(() => dates.map(o => o.id), [dates]);

  const afterChangeDate = (d: moment.Moment | null) => {
    dispatch(aptActions.calendar.getData.fetch({ startTime: d?.format('YYYY-MM-DD') || '' }));
    if (!d) return;
    handleScrollDate(d);
    if (datesListRef.current) {
      const element = datesListRef.current.querySelector(`#BoxTimeDate_${d?.format('DD-MM-YYYY')}`);
      if (element) {
        datesListRef.current.scrollTo({
          left: (element as HTMLElement).offsetLeft,
          behavior: 'smooth',
        });
      }
    }
  };

  const setCalendarDate = (d: Moment | null) => {
    dateParamSignal.value = d;
    dispatch(aptUIActions.setCurrentPage(1));
  };

  const _handleChange = (d: moment.Moment | null) => {
    setSelectedDate(d);
    if (d && !datesString.includes(d?.format('DD-MM-YYYY'))) {
      const _dates = getRangeDates(d.format(), d?.clone().add(1, 'month').format());
      setDates(_dates);
      handleScrollDate(d);
      datesListRef.current?.scrollTo({
        left: 0,
      });
    } else {
      afterChangeDate(d);
    }
    setTimeout(() => {
      setCalendarDate(d);
    }, 500);
  };

  const handleClickDate = (_date: string) => {
    const dateValue = moment(_date, 'DD-MM-YYYY');
    setSelectedDate(dateValue);
    afterChangeDate(dateValue);
    setTimeout(() => {
      setCalendarDate(dateValue);
    }, 500);
  };

  useImperativeHandle(ref, () => ({
    setSelectedDate,
  }));

  return (
    <Row justify={'space-between'} style={{ flex: 1 }}>
      <Row style={{ flex: 1, position: 'relative', height: isModeFull.value ? '65px' : '6rem', }}>
        <Row ref={datesListRef} justify={'space-between'} wrap={false} style={{ position: 'absolute', inset: 0, overflowX: 'scroll' }}>
          <DatesList dates={dates} activeDate={selectedDate?.format('DD-MM-YYYY') || ''} onClickDate={handleClickDate} />
        </Row>
      </Row>
      <WrapDateTime align={'middle'}>
        <div className='icon-right'><IconCalendar /></div>
        <DatePickerStyled
          value={selectedDate}
          onChange={_handleChange}
          suffixIcon={false}
          clearIcon={false}
          allowClear={false}
          inputReadOnly
          format={'LL'}
          disabledDate={(current: MomentType) => current.isBefore(moment().subtract(1, 'day'))}
        />
      </WrapDateTime>
    </Row>
  );
});
DatePicker.displayName = 'DatePickerRef';
export default DatePicker;

const DatesList = memo(({ onClickDate, activeDate, dates }: { onClickDate: (_date: string) => void, activeDate: string, dates: IDateRangeItem[] }) => {

  return (
    <>
      {dates.map(o => (
        <BoxTimeDate key={o.id}
          style={{
            height: isModeFull.value ? '60px' : '5.5rem',
            justifyContent: isModeFull.value ? 'center' : 'space-between',
          }}
          className={activeDate === o.id ? 'active' : ''}
          onClick={() => onClickDate(o.id)}
          id={'BoxTimeDate_' + o.id}
          type='button'
        >
          {!isModeFull.value && <p>{o.ddd}</p>}
          <p className={`date ${isModeFull.value ? 'mode-full' : ''}`}>{o.D}</p>
        </BoxTimeDate>
      ))}
    </>
  );
});

const WrapDateTime = styled(Row)`
border-radius: 5px;
border: 1px solid #7B7B7B;
background: #FFF;
display: flex;
height: 46px;
width: 15rem;
justify-content: space-between;
align-items: center;
flex-shrink: 0;
position: relative;
margin: 0 1.5rem;

.icon-right {
  position: absolute;
  left: 16px;
  display: flex;
}
.ant-picker .ant-picker-input >input {
  font-size: 1rem;
}
`;

const DatePickerStyled = styled(DatePickerAntUI)`
  background: none;
  border: none;
  padding:0;
  flex:1;
  
  input {
    text-align: right;
    color: #1D2129;
    font-family: Poppins;
    font-size: ${() => isModeFull.value ? '18px' : '16px'} !important;
    font-style: normal;
    font-weight: 500;
    line-height: 24px; /* 150% */
    padding: 0px 16px !important;
  }

  &.ant-picker:hover,
  &.ant-picker-focused {
    border: none;
    box-shadow: none;
  }

`;

const BoxTimeDate = styled.button`
  color: #1D2129;
  text-align: center;
  font-family: Poppins;
  font-size: 18px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  min-width: 3.5rem;
  margin-right: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  border-radius: 8px;
  padding-bottom: 8px;
  height: 5.5rem;


  &.active {
    .date {
      border-radius: 100px;
      background: #232F3E;
      color: #fff;
      height: 33px;
      width: 33px;
      display: flex;
      align-items: center;
      justify-content: center;
    }  
  } 

  .date.mode-full {
    height: 50px;
    width: 50px;
    font-size: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &:active {
    background: #e9e8e8;
  }
  &:hover {
    background: #e9e8e8;
  }
`;

